'use client'

import { useMemo } from 'react'
import Image from 'next/image'
import MapCanvas from '@/components/map/MapCanvas'
import {
  boundsFromPois,
  googleMapsSearchUrl,
  isHighlightedPoi,
  isVisiblePoi,
  lastDistanceSegment,
} from '@/components/map/poiHelpers'
import { useLocationMap } from '@/components/map/useLocationMap'
import FadeUp from '@/components/ui/FadeUp'
import RichText from '@/components/ui/RichText'
import { OPENS_IN_NEW_TAB_HINT } from '@/lib/a11y'
import { toExternalHref } from '@/lib/navigation'
import { getStrapiMedia } from '@/lib/strapi/client'
import {
  additionalPoiToMapPoi,
  type MapPoiType,
} from '@/lib/types/map-poi'
import type { TeaserMapType } from '@/lib/types/teaser'
import type { RichTextType } from '@/lib/types/shared'
import { renderWithBr } from '@/lib/utils/render-with-br'
import {
  contentAlignClass,
  isLayoutB,
} from '@/lib/utils/section-styles'

type Props = {
  data: TeaserMapType
  pois?: MapPoiType[]
}

function hasMapDescription(
  description: string | RichTextType | null | undefined,
): boolean {
  if (description == null) return false
  if (typeof description === 'string') return description.trim().length > 0
  return Array.isArray(description) && description.length > 0
}

function MapDescription({
  description,
  className,
}: {
  description: string | RichTextType | null | undefined
  className: string
}) {
  if (!hasMapDescription(description)) return null

  if (Array.isArray(description)) {
    return (
      <div data-fig="teaser-map-description">
        <RichText content={description} className={className} />
      </div>
    )
  }

  const html = typeof description === 'string' ? description.trim() : ''
  if (!html) return null

  return (
    <div
      data-fig="teaser-map-description"
      className={className}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function LocationPinIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 21s6-5.196 6-10a6 6 0 1 0-12 0c0 4.804 6 10 6 10Z"
        fill="currentColor"
      />
      <circle cx="12" cy="11" r="2.5" fill="#F6F0E3" />
    </svg>
  )
}

export default function TeaserMapClient({ data, pois = [] }: Props) {
  const map = data.map
  const lat = map?.coordinates?.latitude
  const lng = map?.coordinates?.longitude
  const hasCoordinates = lat != null && lng != null
  const isVisible = map?.isVisible !== false
  const layoutB = isLayoutB(data.layout)

  const displayPois = useMemo(() => {
    // Layout B is map-only — no collection POI markers/list.
    if (layoutB) return []

    const inlinePois = [...(data.mapPois ?? [])]
      .filter(isVisiblePoi)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    const apiPois = pois
      .filter(isVisiblePoi)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

    const highlighted = pois.filter(isHighlightedPoi)
    const highlightedOrInline =
      data.showHighlightedMapPois === true
        ? highlighted.length > 0
          ? highlighted
          : apiPois
        : inlinePois.length > 0
          ? inlinePois
          : apiPois

    const additional = (data.additionalPois ?? []).map(additionalPoiToMapPoi)

    return [...highlightedOrInline, ...additional]
  }, [
    layoutB,
    pois,
    data.mapPois,
    data.additionalPois,
    data.showHighlightedMapPois,
  ])

  const poiBounds = useMemo(() => boundsFromPois(displayPois), [displayPois])

  const mainMarkerLabel = map?.markerLabel ?? map?.title ?? 'Location'
  const mainMarkerIconUrl =
    getStrapiMedia(map?.markerIcon?.[0]?.url ?? null) || null
  const cloudMapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID?.trim() || ''

  const { containerRef, selectedPoi, focusPoi } = useLocationMap({
    enabled: Boolean(isVisible && hasCoordinates),
    lat: lat ?? 0,
    lng: lng ?? 0,
    mapSkin: map?.mapSkin ?? null,
    cloudMapId,
    mainMarkerIconUrl,
    mainMarkerLabel,
    enableMarker: map?.enableMarker !== false,
    filteredPois: displayPois,
    poiBounds,
  })

  if (!map || !isVisible || !hasCoordinates) return null

  const trimmedDirectionUrl = data.mapDirectionUrl?.trim()
  const directionsHref = trimmedDirectionUrl
    ? (toExternalHref(trimmedDirectionUrl) ?? trimmedDirectionUrl)
    : googleMapsSearchUrl(lat, lng)

  const directionLabel = data.directionLabel?.trim() || null
  const headingId = `teaser-map-heading-${data.id}`
  const configuration = map.configuration ?? data.configuration ?? undefined
  const backgroundColor = configuration?.backgroundColor?.trim() || undefined
  const contentAlignment =
    configuration?.contentAlignment ?? (layoutB ? 'Center' : 'Left')
  const contentAlign = contentAlignClass(contentAlignment)

  if (layoutB) {
    return (
      <section
        data-fig="teaser-map"
        data-layout={data.layout ?? 'Layout B'}
        className={`flex flex-col items-stretch gap-8 overflow-hidden pt-12 md:items-center md:gap-10 md:pt-20${
          backgroundColor ? '' : ' bg-surface-white'
        }`}
        aria-labelledby={map.title ? headingId : undefined}
        style={backgroundColor ? { backgroundColor } : undefined}
      >
        {(map.preTitle || map.title || hasMapDescription(map.description)) && (
          <FadeUp
            className={`mx-auto flex w-full max-w-[715px] flex-col gap-5 px-4 md:gap-2 md:px-0 ${contentAlign}`}
            durationMs={1200}
            delay={100}
          >
            {(map.preTitle || map.title) && (
              <div className="flex w-full flex-col gap-2">
                {map.preTitle && (
                  <p
                    data-fig="teaser-map-pre-heading"
                    className="font-body text-xs font-bold uppercase leading-[150%] tracking-[1px] text-preheading md:text-sm md:leading-5"
                  >
                    {map.preTitle}
                  </p>
                )}
                {map.title && (
                  <h2
                    id={headingId}
                    data-fig="teaser-map-heading"
                    className="w-full font-heading text-[28px] font-bold leading-8 text-[#1C1C1A] md:text-[48px] md:leading-[56px] md:text-ink"
                  >
                    {renderWithBr(map.title)}
                  </h2>
                )}
              </div>
            )}

            <MapDescription
              description={map.description}
              className="w-full font-body text-sm font-normal leading-[22px] text-stone md:text-[20px] md:leading-[160%] md:text-[#282828] [&_a]:underline [&_p]:m-0"
            />
          </FadeUp>
        )}

        <div className="relative h-[320px] w-full shrink-0 md:h-[583px]">
          <MapCanvas
            containerRef={containerRef}
            mapsLink={directionsHref}
            label={mainMarkerLabel}
            showCta={false}
            className="absolute inset-0 h-full w-full"
          />
        </div>

        {directionLabel && (
          <a
            href={directionsHref}
            target="_blank"
            rel="noopener noreferrer"
            data-fig="teaser-map-directions"
            className="mx-auto inline-flex items-center gap-2 px-4 pb-12 font-body text-base font-semibold leading-6 text-ink transition-colors hover:text-accent focus-visible:outline-none focus-visible:underline md:pb-16"
          >
            {directionLabel}
            <span className="sr-only">{OPENS_IN_NEW_TAB_HINT}</span>
            <span aria-hidden="true" className="text-2xl leading-none">
              →
            </span>
          </a>
        )}
      </section>
    )
  }

  return (
    <section
      data-fig="teaser-map"
      data-layout={data.layout ?? 'Layout A'}
      className={`overflow-hidden${backgroundColor ? '' : ' bg-[#F4F1EE]'}`}
      aria-labelledby={map.title ? headingId : undefined}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="flex flex-col md:flex-row md:min-h-[856px]">
        {/* Keep map outside FadeUp — parent transforms collapse map overlays. */}
        <div className="relative min-h-[280px] w-full min-w-0 flex-1 basis-0 md:min-h-[856px]">
          <MapCanvas
            containerRef={containerRef}
            mapsLink={directionsHref}
            label={mainMarkerLabel}
            showCta={false}
            className="absolute inset-0 h-full w-full"
          />
        </div>

        <FadeUp
          className={`flex w-full min-w-0 flex-1 basis-0 flex-col justify-center gap-6 px-5 pb-10 pt-8 md:gap-8 md:py-20 md:pl-12 md:pr-16 ${contentAlign}`}
          durationMs={1200}
          delay={200}
        >
          {(map.preTitle || map.title || hasMapDescription(map.description)) && (
            <div className="flex w-full flex-col gap-2 md:gap-4">
              {map.preTitle && (
                <p
                  data-fig="teaser-map-pre-heading"
                  className="font-body text-xs font-bold uppercase leading-[150%] tracking-[1px] text-preheading md:text-sm md:leading-5"
                >
                  {map.preTitle}
                </p>
              )}
              {map.title && (
                <h2
                  id={headingId}
                  data-fig="teaser-map-heading"
                  className="font-heading text-[30px] font-bold leading-[120%] text-ink md:text-[48px] md:leading-[56px]"
                >
                  {renderWithBr(map.title)}
                </h2>
              )}
              <MapDescription
                description={map.description}
                className="font-body text-sm font-normal leading-[150%] text-muted md:text-base md:leading-6 [&_a]:underline [&_p]:m-0"
              />
            </div>
          )}

          {displayPois.length > 0 && (
            <ul
              className="flex w-full flex-col gap-4 md:gap-5"
              aria-label="Nearby highlights"
            >
              {displayPois.map((poi) => {
                const iconUrl = getStrapiMedia(poi.category?.icon?.url ?? null)
                const travelTime = lastDistanceSegment(poi.distanceDetails?.text)
                const selected = selectedPoi?.id === poi.id
                const subtitle = poi.category?.name ?? poi.description

                return (
                  <li
                    key={poi.id}
                    className="border-b border-[#CED8C6] py-3 md:py-4"
                  >
                    <button
                      type="button"
                      onClick={() => focusPoi(poi)}
                      aria-current={selected ? 'true' : undefined}
                      className="flex w-full items-center justify-between gap-3 text-left transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:underline md:gap-4"
                    >
                      <span className="flex min-w-0 items-center gap-3 md:gap-4">
                        <span
                          className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F0E6DB] text-accent md:size-10"
                          aria-hidden="true"
                        >
                          {iconUrl ? (
                            <Image
                              src={iconUrl}
                              alt=""
                              width={24}
                              height={24}
                              className="size-4 object-contain"
                            />
                          ) : (
                            <LocationPinIcon />
                          )}
                        </span>

                        <span className="flex min-w-0 flex-col gap-0.5 md:gap-0">
                          {poi.title && (
                            <span className="block font-body text-[15px] font-semibold leading-5 text-ink md:text-base md:leading-6">
                              {poi.title}
                            </span>
                          )}
                          {subtitle && (
                            <span className="block font-body text-xs font-normal leading-4 text-muted md:text-sm md:leading-5">
                              {subtitle}
                            </span>
                          )}
                        </span>
                      </span>

                      {travelTime && (
                        <span className="shrink-0 font-body text-sm font-semibold leading-[18px] text-ink md:text-base md:leading-6">
                          {travelTime}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {directionLabel && (
            <a
              href={directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              data-fig="teaser-map-directions"
              className="inline-flex items-center gap-2 self-start font-body text-base font-semibold leading-6 text-ink transition-colors hover:text-accent focus-visible:outline-none focus-visible:underline"
            >
              {directionLabel}
              <span className="sr-only">{OPENS_IN_NEW_TAB_HINT}</span>
              <span aria-hidden="true" className="text-2xl leading-none">
                →
              </span>
            </a>
          )}
        </FadeUp>
      </div>
    </section>
  )
}
